// The website's "Get started" form: what you do, about you, then create an account or book a call.
// Ported from the new marketing site's demo-core so both send the CRM exactly the same request
// (intake schema v2). Pure: no React, no network.

import { FormAttribution, formAttribution, StoredAttribution } from '../../utilities/attribution';

export type Persona = 'wallpaper_brand' | 'artist' | 'interior_designer' | 'retailer' | 'other';
export type Product = 'wallpaper' | 'infinity_murals' | 'wrapping_paper';
export type Door = 'signup' | 'book';

export const PERSONAS: [Persona, string][] = [
    ['wallpaper_brand', 'Wallpaper brand'], ['artist', 'Artist'], ['interior_designer', 'Interior designer'], ['retailer', 'Retailer'], ['other', 'Other']
];
export const PRODUCTS: [Product, string][] = [['wallpaper', 'Wallpaper'], ['infinity_murals', 'Infinity Murals'], ['wrapping_paper', 'Wrapping Paper']];
export const PLATFORMS = ['Shopify', 'Etsy', 'Spoonflower', 'WooCommerce', 'Another platform', 'Through client projects', 'Not selling yet'] as const;
export const VOLUMES = ['Just exploring', '1–10', '11–50', '51–200', '201+', 'Project-based / not sure'] as const;

export interface GetStartedValues {
    personas: Persona[];
    product_interests: Product[];
    ecommerce_platform: string;
    volume_band: string;
    first_name: string;
    last_name: string;
    email: string;
    company_name: string;
    website: string;
    /** Required, in any format: the CRM reads it as E.164 when it can, otherwise keeps it as typed. */
    phone: string;
    country: string;
    marketing_consent: boolean;
}

export const EMPTY_VALUES: GetStartedValues = {
    personas: [], product_interests: [], ecommerce_platform: '', volume_band: '',
    first_name: '', last_name: '', email: '', company_name: '', website: '', phone: '', country: '', marketing_consent: false
};

const LIMITS: Partial<Record<keyof GetStartedValues, number>> = { first_name: 100, last_name: 100, email: 254, company_name: 160, country: 100, website: 500 };
export const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EMAIL = /^(?!\.)(?!.*\.\.)[A-Z0-9_'+\-.]*[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

// #region normalizeWebsite
/** "mybrand.com" → "https://mybrand.com/". Throws a visitor-readable message for anything that isn't a web address. */
export const normalizeWebsite = (value: string): string => {
    const raw = value.trim();
    if (!raw) return '';
    if (/\s/.test(raw) || (/^[a-z][a-z\d+.-]*:/i.test(raw) && !/^https?:\/\//i.test(raw))) throw new Error('Enter a domain like yourbrand.com.');
    let u: URL;
    try { u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/\//, '')}`); } catch { throw new Error('Enter a domain like yourbrand.com.'); }
    const labelsOk = u.hostname.split('.').every((label) => /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label));
    if (u.username || u.password || !u.hostname.includes('.') || !labelsOk) throw new Error('Enter a domain like yourbrand.com.');
    if (u.href.length > 500) throw new Error('Please use 500 characters or fewer.');
    return u.href;
};

// #region phoneProblem
/** Required, but any format goes: only something with fewer than 7 digits is turned back. */
export const phoneProblem = (phone: string): string | null => {
    if (!phone.trim()) return 'Please add a phone number.';
    return (phone.match(/\d/g) ?? []).length < 7 ? 'Enter a number we can reach you on.' : null;
};

// #region validateStep
/** Problems with one step's answers, by field. Step 0 is "what you do", step 1 "about you". */
export const validateStep = (step: 0 | 1, v: GetStartedValues): Partial<Record<keyof GetStartedValues, string>> => {
    const errors: Partial<Record<keyof GetStartedValues, string>> = {};
    if (step === 0) {
        if (!v.personas.length) errors.personas = 'Select at least one role.';
        if (!(PLATFORMS as readonly string[]).includes(v.ecommerce_platform)) errors.ecommerce_platform = 'Please choose an option.';
        if (!(VOLUMES as readonly string[]).includes(v.volume_band)) errors.volume_band = 'Please choose an option.';
        return errors;
    }
    for (const key of ['first_name', 'last_name', 'email', 'company_name'] as const) if (!v[key].trim()) errors[key] = 'Please complete this field.';
    for (const [key, max] of Object.entries(LIMITS) as [keyof GetStartedValues, number][]) {
        if (!errors[key] && String(v[key]).trim().length > max) errors[key] = `Please use ${max} characters or fewer.`;
    }
    if (!errors.email && !EMAIL.test(v.email.trim())) errors.email = 'Enter a valid email address.';
    try { normalizeWebsite(v.website); } catch (err) { errors.website = (err as Error).message; }
    const phone = phoneProblem(v.phone);
    if (phone) errors.phone = phone;
    return errors;
};

// #region doorOrder
/** Interior designers, project work and bigger volumes usually want a conversation first; everyone else, the account. */
export const doorOrder = (v: GetStartedValues): Door[] => {
    const talk = v.personas[0] === 'interior_designer' || ['51–200', '201+', 'Project-based / not sure'].includes(v.volume_band)
        || v.ecommerce_platform === 'Through client projects';
    return talk ? ['book', 'signup'] : ['signup', 'book'];
};

export type Attribution = FormAttribution;

// #region attributionFrom
/**
 * UTM tags, ad click ids, the host page and its referrer as the embed loader passed them on the page
 * address, plus the visitor's remembered first and latest touch (see utilities/attribution).
 */
export const attributionFrom = (params: URLSearchParams, stored?: StoredAttribution): Attribution =>
    formAttribution(params, 'get_started_embed', stored);

// #region phoneField
/** The phone as typed (the CRM tidies the format), left out when blank. Shared with the booking page. */
export const phoneField = (phone: string): { phone?: string } => {
    const typed = phone.trim().slice(0, 50);
    return typed ? { phone: typed } : {};
};

// #region payloadFor
/** The CRM intake request (schema v2) for the door they chose. */
export const payloadFor = (v: GetStartedValues, submissionId: string, attribution: Attribution, door: Door): Record<string, unknown> => {
    if (!UUID.test(submissionId)) throw new Error('Invalid submission id');
    return {
        schema_version: 2, intent: door === 'signup' ? 'signup' : 'booking', submission_id: submissionId,
        first_name: v.first_name.trim(), last_name: v.last_name.trim(), email: v.email.trim().toLowerCase(),
        company_name: v.company_name.trim(), country: v.country.trim(), website: normalizeWebsite(v.website),
        ...phoneField(v.phone),
        personas: [...v.personas], product_interests: [...v.product_interests],
        ecommerce_platform: v.ecommerce_platform, volume_band: v.volume_band,
        goals_note: '', preferred_timing: '', material_interest: null,
        contact_consent: true, marketing_consent: v.marketing_consent,
        attribution
    };
};

export interface DemoNext {
    kind: 'book' | 'self_serve';
    team_slug?: string;
    booking_url?: string;
    signup_url?: string;
}

// #region nextPath
/**
 * Where the frame goes after the CRM accepted the request: the password step or the team calendar,
 * on this same site. The request id rides in the query; name, email and brand only in the fragment,
 * which never reaches a server log.
 */
export const nextPath = (next: DemoNext, submissionId: string, v: GetStartedValues, embedded: boolean): string => {
    const query = new URLSearchParams({ r: submissionId });
    if (embedded) query.set('embed', '1');
    if (next.kind === 'book') {
        const slug = next.team_slug && /^[a-z0-9-]{1,100}$/.test(next.team_slug) ? next.team_slug : 'wallmates-sales';
        const hash = new URLSearchParams({ name: `${v.first_name.trim()} ${v.last_name.trim()}`.trim().slice(0, 201), email: v.email.trim().slice(0, 254) });
        return `/book/team/${slug}?${query.toString()}#${hash.toString()}`;
    }
    const hash = new URLSearchParams({ first: v.first_name.trim().slice(0, 100), last: v.last_name.trim().slice(0, 100), email: v.email.trim().slice(0, 254), company: v.company_name.trim().slice(0, 160) });
    return `/new-account?${query.toString()}#${hash.toString()}`;
};

// #region newSubmissionId
export const newSubmissionId = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); });

// #region errorMessage
export const errorMessage = (status: number): string => ({
    400: 'Please check your details. Your answers are still here.',
    413: 'That was too long to send. Please shorten your answers.',
    429: 'Too many attempts. Please try again shortly.',
    503: 'We couldn’t save that just now. Your answers are saved, so please try again.'
} as Record<number, string>)[status] ?? 'We couldn’t confirm that went through. Your answers are saved, and trying again is safe.';
